import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, NgZone } from '@angular/core';
import { Observable, from } from 'rxjs';
import { environment } from '../../environments/environment';

declare global {
    interface Window {
        grecaptcha: any;
    }
}

@Injectable({
    providedIn: 'root'
})
export class ReCaptchaService {

    private scriptLoadingPromise?: Promise<void>;
    private readonly scriptId = 'google-recaptcha-v3-script';

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private ngZone: NgZone
    ) { }

    generateToken(action: string = 'submit'): Observable<string> {
        return from(this.generateTokenAsync(action));
    }

    async generateTokenAsync(action: string = 'submit'): Promise<string> {
        if (!environment.recaptchaSiteKey) {
            throw new Error('Recaptcha site key is missing');
        }

        await this.loadScriptIfNeeded();
        await this.waitUntilReady();

        const token = await window.grecaptcha.execute(
            environment.recaptchaSiteKey,
            { action }
        );

        if (!token) {
            throw new Error('Failed to generate recaptcha token');
        }

        return token;
    }

    public loadScriptIfNeeded(): Promise<void> {
        if (this.scriptLoadingPromise) {
            return this.scriptLoadingPromise;
        }

        const existingScript = this.document.getElementById(this.scriptId);

        if (existingScript) {
            this.scriptLoadingPromise = Promise.resolve();
            return this.scriptLoadingPromise;
        }

        this.scriptLoadingPromise = new Promise<void>((resolve, reject) => {
            const script = this.document.createElement('script');

            script.id = this.scriptId;
            script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(environment.recaptchaSiteKey)}`;
            script.async = true;
            script.defer = true;

            script.onload = () => resolve();

            script.onerror = () => {
                this.scriptLoadingPromise = undefined;
                reject(new Error('Failed to load recaptcha script'));
            };

            this.document.head.appendChild(script);
        });

        return this.scriptLoadingPromise;
    }

    private waitUntilReady(): Promise<void> {
        return new Promise((resolve, reject) => {
            let attempts = 0;
            const maxAttempts = 50;

            const check = () => {
                attempts++;

                if (window.grecaptcha && typeof window.grecaptcha.ready === 'function') {
                    window.grecaptcha.ready(() => {
                        this.ngZone.run(() => resolve());
                    });
                    return;
                }

                if (attempts >= maxAttempts) {
                    reject(new Error('Recaptcha is not ready'));
                    return;
                }

                setTimeout(check, 100);
            };

            check();
        });
    }
}